// Position d'un signalement : choisie d'un clic sur la carte de la collectivité, relevée par
// l'appareil (géolocalisation) ou saisie en coordonnées ; l'état de chaque voie est écrit.
import { Bouton, CarteTerritoire, Champ, Squelette } from '@cc/ui';
import { LocateFixed } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { construireEchelles, fichierPourEchelle, fichiersInitiaux, useLimites, type NomFichier } from '../../territoire/donnees';
import { useTerritoirePublic } from './api';
import styles from '../citoyen.module.css';

export type Position = { longitude: number; latitude: number };

function Carte({ code, position, onPosition }: { code: string; position: Position | null; onPosition: (p: Position) => void }) {
  const territoire = useTerritoirePublic(code);
  const [supplementaires, setSupplementaires] = useState<NomFichier[]>([]);
  const fichiers = useMemo(() => [...new Set([...fichiersInitiaux(territoire.data), ...supplementaires])], [territoire.data, supplementaires]);
  const { limites, presentation, chargement } = useLimites(fichiers, fichiersInitiaux(territoire.data));
  const echelles = useMemo(() => (territoire.data && !chargement ? construireEchelles(territoire.data, limites, presentation) : null), [territoire.data, chargement, limites, presentation]);
  const [echelle, setEchelle] = useState<string | null>(null);
  useEffect(() => {
    if (echelles && !echelle) setEchelle(echelles.defaut);
  }, [echelles, echelle]);
  if (territoire.isError) return <p className={styles.aide}>Carte indisponible : utilisez la position de l&apos;appareil ou saisissez les coordonnées.</p>;
  if (!echelles || !echelle) return <Squelette hauteurBloc={260} libelle="Chargement de la carte" />;
  return (
    <>
      <CarteTerritoire
        titre="Lieu du problème : touchez la carte pour le placer"
        echelles={echelles.echelles}
        echelle={echelle}
        onEchelle={(id) => {
          const f = fichierPourEchelle(id);
          if (f) setSupplementaires((s) => (s.includes(f) ? s : [...s, f]));
          setEchelle(id);
        }}
        marqueurs={position ? [{ id: 'signalement', numero: 1, longitude: position.longitude, latitude: position.latitude, libelle: 'Lieu signalé', ton: 'retard' }] : []}
        onSaisie={(p) => onPosition({ longitude: Math.round(p[0] * 1e6) / 1e6, latitude: Math.round(p[1] * 1e6) / 1e6 })}
        compact
        hauteur={280}
        libellesTon={{ retard: 'Lieu signalé' }}
        libelleObjets={{ singulier: 'lieu signalé', pluriel: 'lieux signalés' }}
      />
      {echelles.mention ? <p className={styles.aide}>{echelles.mention}</p> : null}
    </>
  );
}

export function SaisiePosition({ code, position, onPosition, erreur }: { code: string; position: Position | null; onPosition: (p: Position | null) => void; erreur?: string | null }) {
  const [etat, setEtat] = useState<string | null>(null);
  const [lon, setLon] = useState(position ? String(position.longitude) : '');
  const [lat, setLat] = useState(position ? String(position.latitude) : '');
  useEffect(() => {
    setLon(position ? String(position.longitude) : '');
    setLat(position ? String(position.latitude) : '');
  }, [position]);
  const localiser = () => {
    if (!navigator.geolocation) {
      setEtat('La localisation n\'est pas disponible sur cet appareil : placez le lieu sur la carte ou saisissez les coordonnées.');
      return;
    }
    setEtat('Recherche de la position…');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        onPosition({ longitude: Math.round(p.coords.longitude * 1e6) / 1e6, latitude: Math.round(p.coords.latitude * 1e6) / 1e6 });
        setEtat('Position de l\'appareil relevée.');
      },
      () => setEtat('Position refusée ou indisponible : placez le lieu sur la carte ou saisissez les coordonnées.'),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };
  const saisir = (l: string, a: string) => {
    setLon(l);
    setLat(a);
    const x = Number(l);
    const y = Number(a);
    onPosition(l !== '' && a !== '' && Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) <= 180 && Math.abs(y) <= 90 ? { longitude: x, latitude: y } : null);
  };
  return (
    <fieldset className={styles.pile}>
      <legend>Lieu du problème</legend>
      <Carte code={code} position={position} onPosition={(p) => { onPosition(p); setEtat('Lieu placé sur la carte.'); }} />
      <div>
        <Bouton variante="secondaire" petit icone={LocateFixed} onClick={localiser}>
          Utiliser la position de mon appareil
        </Bouton>
      </div>
      {etat ? <p className={styles.aide} role="status">{etat}</p> : null}
      <Champ id="sig-latitude" libelle="Latitude" type="number" step="any" inputMode="decimal" value={lat} onChange={(e) => saisir(lon, e.target.value)} erreur={erreur ?? null} />
      <Champ id="sig-longitude" libelle="Longitude" type="number" step="any" inputMode="decimal" value={lon} onChange={(e) => saisir(e.target.value, lat)} />
    </fieldset>
  );
}
